import { z } from 'zod';
import { IsoTimestamp } from '../common.js';
/**
 * Specialisation taxonomy — first-class Firestore-backed records that
 * replace the hardcoded CANONICAL_SPECIALISATIONS / CANONICAL_PRODUCT_-
 * SPECIALISATIONS string arrays in `sociale-kaart.ts`. Documents,
 * Confluence sources and Code sources can each carry an array of
 * specialisation NAMES (not ids) so retrieval / chip-rail code that
 * already reads `specialisations: string[]` keeps working unchanged.
 *
 * Top-level collection: `specialisations/{id}`. Cross-tenant; kb_admin
 * RBAC at write time, all-authenticated read. Shared-corpus exception is
 * implicit — the vocabulary applies to every tenant's tagging surface.
 *
 * Ring split is preserved (each spec belongs to `ring0` HR vocab OR
 * `ring1` product vocab) so the inline editor's ring-scoped picker on
 * /admin/documents keeps showing only the relevant tags.
 *
 * Migration: the legacy hardcoded names are seeded into this collection
 * by `backend/scripts/seed-specialisations.ts` with default colours +
 * owner = 'system'. Items already tagged by name continue to resolve
 * because we match on name strings, not ids.
 */
export const Specialisation = z.object({
    /** Stable slug; kebab-case, lowercase, derived from name on create. */
    id: z
        .string()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'Specialisation id must be kebab-case lowercase.'),
    /** Display name. Unique case-insensitively across the collection. */
    name: z.string().min(1).max(60),
    /** Short blurb shown in the master list + detail header. */
    description: z.string().max(500).default(''),
    /** Hex colour for the chip / list rail accent. */
    colour: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Colour must be a 6-digit hex string, e.g. #3B5AA8.'),
    /** Which ring's vocabulary this belongs to. */
    ring: z.enum(['ring0', 'ring1']),
    /** Free-text owner — usually a team or person ("Compliance", "Andre W."). */
    owner: z.string().max(120).default(''),
    createdAt: IsoTimestamp,
    createdBy: z.string().min(1).max(200),
    updatedAt: IsoTimestamp,
    updatedBy: z.string().min(1).max(200),
});
/** Body for POST /api/v1/admin/specialisations. */
export const SpecialisationCreateInput = z.object({
    name: z.string().min(1).max(60).trim(),
    description: z.string().max(500).trim().default(''),
    colour: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    ring: z.enum(['ring0', 'ring1']),
    owner: z.string().max(120).trim().default(''),
});
/** Body for PATCH /api/v1/admin/specialisations/:id — all fields optional. */
export const SpecialisationUpdateInput = z
    .object({
    name: z.string().min(1).max(60).trim().optional(),
    description: z.string().max(500).trim().optional(),
    colour: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    owner: z.string().max(120).trim().optional(),
    // ring is NOT editable post-create — it determines which inline editor
    // surface the spec shows up in, and re-homing existing assignments
    // across rings is out of scope for v1.
})
    .refine((b) => Object.keys(b).length > 0, { message: 'No fields to update.' });
/** Body for POST /api/v1/admin/specialisations/:id/merge. */
export const SpecialisationMergeInput = z.object({
    /** Target specialisation id — every item tagged with the source's name
     *  is retagged to the target's name. Source is deleted on success. */
    intoId: z
        .string()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9-]*$/),
});
/** Item-collection kinds the assign/unassign and members endpoints operate on. */
export const SpecialisationItemKind = z.enum(['documents', 'confluence', 'code']);
/** Body for assign / unassign. */
export const SpecialisationAssignInput = z.object({
    kind: SpecialisationItemKind,
    /** Filenames for documents, source ids for confluence + code. */
    ids: z.array(z.string().min(1).max(400)).min(1).max(200),
});
/** Stats row used by the admin page's KPI strip + master-list count pips. */
export const SpecialisationStats = z.object({
    name: z.string(),
    documents: z.number().int().nonnegative(),
    confluence: z.number().int().nonnegative(),
    code: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
//# sourceMappingURL=specialisation.js.map